<template>
    <div class="layout-editor">
        <v-sheet
            class="layout-editor__toolbar"
            :style="{ top: stickyTop + 'px' }"
            outlined
        >
            <v-btn
                icon
                small
                :title="$t('layoutEditor.undo')"
                :disabled="!canUndo || busy"
                @mousedown.prevent
                @click="undo"
            >
                <v-icon>mdi-undo</v-icon>
            </v-btn>
            <v-btn
                icon
                small
                :title="$t('layoutEditor.redo')"
                :disabled="!canRedo || busy"
                @mousedown.prevent
                @click="redo"
            >
                <v-icon>mdi-redo</v-icon>
            </v-btn>

            <v-divider
                vertical
                class="mx-1"
            />

            <v-menu
                offset-y
                attach
            >
                <template #activator="{ on, attrs }">
                    <v-btn
                        text
                        small
                        :title="$t('layoutEditor.textStyle')"
                        :disabled="busy"
                        v-bind="attrs"
                        @mousedown.prevent
                        v-on="on"
                    >
                        <v-icon left>
                            mdi-format-header-pound
                        </v-icon>
                        {{ $t('layoutEditor.textStyle') }}
                        <v-icon right>
                            mdi-chevron-down
                        </v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item
                        v-for="style in textStyles"
                        :key="style.tag"
                        @mousedown.prevent
                        @click="setTextStyle(style.tag)"
                    >
                        <v-list-item-title :class="style.class">
                            {{ $t('layoutEditor.' + style.label) }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-btn
                v-for="action in inlineActions"
                :key="action.command"
                icon
                small
                :title="$t(action.title)"
                :disabled="busy"
                @mousedown.prevent
                @click="format(action.command)"
            >
                <v-icon>{{ action.icon }}</v-icon>
            </v-btn>

            <v-menu
                offset-y
                attach
            >
                <template #activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        :title="$t('layoutEditor.color')"
                        :disabled="busy"
                        v-bind="attrs"
                        @mousedown.prevent
                        v-on="on"
                    >
                        <v-icon>mdi-format-color-text</v-icon>
                    </v-btn>
                </template>
                <v-sheet
                    class="layout-editor__swatches"
                    @mousedown.prevent
                >
                    <button
                        v-for="color in colors"
                        :key="color"
                        type="button"
                        class="layout-editor__swatch"
                        :style="{ background: color }"
                        :title="color"
                        @click="setColor(color)"
                    />
                </v-sheet>
            </v-menu>

            <v-btn
                icon
                small
                :title="$t('layoutEditor.clean')"
                :disabled="busy"
                @mousedown.prevent
                @click="format('removeFormat')"
            >
                <v-icon>mdi-format-clear</v-icon>
            </v-btn>

            <v-divider
                vertical
                class="mx-1"
            />

            <v-btn
                v-for="action in blockActions"
                :key="action.command"
                icon
                small
                :title="$t(action.title)"
                :disabled="busy"
                @mousedown.prevent
                @click="formatBlock(action.command)"
            >
                <v-icon>{{ action.icon }}</v-icon>
            </v-btn>

            <v-divider
                vertical
                class="mx-1"
            />

            <v-btn
                icon
                small
                :title="$t('layoutEditor.link')"
                :disabled="busy"
                @mousedown.prevent
                @click="openLinkDialog"
            >
                <v-icon>mdi-link-variant</v-icon>
            </v-btn>

            <v-divider
                vertical
                class="mx-1"
            />

            <v-btn
                text
                small
                :title="$t('layoutEditor.insertImageHint')"
                :disabled="busy"
                @mousedown.prevent
                @click="pickImage('insert')"
            >
                <v-icon left>
                    mdi-image-plus
                </v-icon>
                {{ $t('layoutEditor.insertImage') }}
            </v-btn>

            <template v-if="activeBlock">
                <v-divider
                    vertical
                    class="mx-1"
                />
                <v-btn
                    text
                    small
                    :title="$t('layoutEditor.duplicateBlockHint')"
                    :disabled="busy"
                    @mousedown.prevent
                    @click="duplicateBlock"
                >
                    <v-icon left>
                        mdi-content-duplicate
                    </v-icon>
                    {{ $t('layoutEditor.duplicateBlock') }}
                </v-btn>
                <v-btn
                    text
                    small
                    :title="$t('layoutEditor.removeBlockHint')"
                    :disabled="busy"
                    @mousedown.prevent
                    @click="removeBlock"
                >
                    <v-icon left>
                        mdi-delete-outline
                    </v-icon>
                    {{ $t('layoutEditor.removeBlock') }}
                </v-btn>
            </template>

            <input
                ref="imageInput"
                type="file"
                :accept="imageTypes"
                hidden
                @change="onImagePicked"
            >
        </v-sheet>

        <p class="layout-editor__help text-caption mb-2">
            {{ $t('layoutEditor.help') }}
        </p>

        <div
            ref="stage"
            class="layout-editor__stage"
        >
            <v-progress-linear
                v-if="busy"
                indeterminate
                absolute
                top
                color="#0646ed"
            />

            <!-- El contenido se gestiona a mano (innerHTML), nunca desde la
                 plantilla: así el HTML guardado no pasa por ningún modelo que lo
                 reescriba y lo que no se toca se queda exactamente igual. -->
            <div
                ref="content"
                class="ql-editor layout-editor__content"
                contenteditable="true"
                @beforeinput="onBeforeInput"
                @input="onInput"
                @keydown="onKeydown"
                @click.capture="onContentClick"
                @paste="onPaste"
                @dragover="onDragOver"
                @drop="onDrop"
            />

            <template v-if="selectedImage">
                <div
                    class="layout-editor__frame"
                    :style="frameStyle"
                />
                <v-sheet
                    class="layout-editor__image-bar"
                    elevation="4"
                    :style="imageBarStyle"
                    @mousedown.prevent
                >
                    <v-btn
                        text
                        small
                        :disabled="busy"
                        @click="pickImage('replace')"
                    >
                        <v-icon left>
                            mdi-image-edit-outline
                        </v-icon>
                        {{ $t('layoutEditor.replaceImage') }}
                    </v-btn>
                    <v-btn
                        icon
                        small
                        :title="$t('layoutEditor.smaller')"
                        :disabled="busy"
                        @click="resizeImage(-1)"
                    >
                        <v-icon>mdi-magnify-minus-outline</v-icon>
                    </v-btn>
                    <span class="layout-editor__size">{{ selectedWidth }} px</span>
                    <v-btn
                        icon
                        small
                        :title="$t('layoutEditor.bigger')"
                        :disabled="busy"
                        @click="resizeImage(1)"
                    >
                        <v-icon>mdi-magnify-plus-outline</v-icon>
                    </v-btn>
                    <v-btn
                        text
                        small
                        color="error"
                        :disabled="busy"
                        @click="removeImage"
                    >
                        <v-icon left>
                            mdi-delete-outline
                        </v-icon>
                        {{ $t('layoutEditor.removeImage') }}
                    </v-btn>
                </v-sheet>
            </template>
        </div>

        <v-dialog
            v-model="link.dialog"
            max-width="560"
        >
            <v-card>
                <v-card-title class="text-h6">
                    {{ $t('layoutEditor.link') }}
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="link.href"
                        :label="$t('layoutEditor.linkUrl')"
                        :error-messages="link.error"
                        autofocus
                        @input="link.error = ''"
                    />
                    <v-btn
                        text
                        small
                        :loading="link.uploading"
                        @click="$refs.linkFileInput.click()"
                    >
                        <v-icon left>
                            mdi-file-upload-outline
                        </v-icon>
                        {{ $t('layoutEditor.linkUpload') }}
                    </v-btn>
                    <input
                        ref="linkFileInput"
                        type="file"
                        :accept="imageTypes + ',application/pdf'"
                        hidden
                        @change="onLinkFilePicked"
                    >
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        v-if="link.anchor"
                        text
                        color="error"
                        @click="removeLink"
                    >
                        {{ $t('layoutEditor.linkRemove') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        text
                        @click="link.dialog = false"
                    >
                        {{ $t('common.confirmDialogCancel') }}
                    </v-btn>
                    <v-btn
                        text
                        color="blue darken-1"
                        :disabled="link.uploading"
                        @click="applyLink"
                    >
                        {{ $t('layoutEditor.linkApply') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { uploadEditorFile } from '@/helpers/editorFiles'

/**
 * Editor visual del panel (páginas, artículos y términos), tanto para contenidos
 * sencillos como para los que tienen maquetación propia (columnas, rejillas,
 * estilos en línea), como la página de premios.
 *
 * Sustituye a Quill, que solo entiende párrafos sueltos y aplana cualquier
 * rejilla. Aquí se edita el HTML real con la edición nativa del navegador: lo
 * que no se toca se guarda byte a byte igual. Encima van las protecciones para
 * que un cliente que no sabe HTML no pueda romper el diseño sin querer:
 *
 *  - Borrar o escribir sobre una selección que mezcla dos bloques del diseño
 *    (dos tarjetas de la rejilla, o una tarjeta y el texto de fuera) se bloquea:
 *    es lo que fusionaría un premio con otro.
 *  - Intro dentro de un bloque mete un salto de línea en vez de partirlo en dos.
 *  - Lo pegado entra como texto plano; las imágenes pegadas o arrastradas se
 *    suben al servidor en vez de incrustarse en base64.
 *  - Las imágenes y los bloques se cambian, duplican o quitan con botones.
 *
 * El historial de deshacer es propio: los cambios hechos con botones tocan el DOM
 * directamente y el deshacer nativo del navegador no los conoce.
 */

const IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp'

// Bloques que delimitan el diseño: nada puede fusionarse por encima de ellos.
const LAYOUT_BLOCKS = ['DIV', 'TD', 'TH']

// Bloques de texto que sí se pueden partir con Intro, como en cualquier editor.
const TEXT_BLOCKS = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE']

// Bloques que no pueden ir dentro de un párrafo o un título: el navegador, al
// leer el HTML guardado, los saca afuera.
const PARAGRAPH_BREAKERS = ['P', 'UL', 'OL', 'DIV', 'BLOCKQUOTE', 'PRE', 'TABLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']
const NESTED_BLOCKS_SELECTOR = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']
    .map(parent => PARAGRAPH_BREAKERS.map(child => parent + ' > ' + child.toLowerCase()).join(', '))
    .join(', ')

const TEXT_STYLES = [
    { tag: 'P', label: 'styleNormal', class: '' },
    { tag: 'H1', label: 'styleTitle1', class: 'text-h5' },
    { tag: 'H2', label: 'styleTitle2', class: 'text-h6' },
    { tag: 'H3', label: 'styleTitle3', class: 'text-subtitle-1 font-weight-bold' }
]

const INLINE_ACTIONS = [
    { command: 'bold', icon: 'mdi-format-bold', title: 'layoutEditor.bold' },
    { command: 'italic', icon: 'mdi-format-italic', title: 'layoutEditor.italic' },
    { command: 'underline', icon: 'mdi-format-underline', title: 'layoutEditor.underline' },
    { command: 'strikeThrough', icon: 'mdi-format-strikethrough-variant', title: 'layoutEditor.strike' }
]

const BLOCK_ACTIONS = [
    { command: 'justifyLeft', icon: 'mdi-format-align-left', title: 'layoutEditor.alignLeft' },
    { command: 'justifyCenter', icon: 'mdi-format-align-center', title: 'layoutEditor.alignCenter' },
    { command: 'justifyRight', icon: 'mdi-format-align-right', title: 'layoutEditor.alignRight' },
    { command: 'insertUnorderedList', icon: 'mdi-format-list-bulleted', title: 'layoutEditor.bulletList' },
    { command: 'insertOrderedList', icon: 'mdi-format-list-numbered', title: 'layoutEditor.numberedList' }
]

// Negro, el azul corporativo del portal y unos pocos básicos.
const COLORS = ['#000000', '#0646ed', '#0a1f44', '#5f6368', '#d32f2f', '#ef6c00', '#2e7d32', '#6a1b9a']

const HISTORY_LIMIT = 100
const MERGE_MS = 1000
const MIN_IMAGE_WIDTH = 40
const DEFAULT_MAX_IMAGE_WIDTH = 600

export default {
    name: 'LayoutEditor',
    props: {
        value: {
            type: String,
            default: ''
        },
        // (file) => Promise<url>: sube la imagen o el PDF y devuelve su enlace.
        upload: {
            type: Function,
            default: uploadEditorFile
        }
    },
    data: () => ({
        imageTypes: IMAGE_TYPES,
        textStyles: TEXT_STYLES,
        inlineActions: INLINE_ACTIONS,
        blockActions: BLOCK_ACTIONS,
        colors: COLORS,
        selectedImage: null,
        selectedWidth: 0,
        frame: { top: 0, left: 0, width: 0, height: 0 },
        activeBlock: null,
        busy: false,
        stickyTop: 64,
        historyIndex: 0,
        historyLength: 1,
        link: {
            dialog: false,
            href: '',
            anchor: null,
            range: null,
            uploading: false,
            error: ''
        }
    }),
    computed: {
        canUndo() {
            return this.historyIndex > 0
        },
        canRedo() {
            return this.historyIndex < this.historyLength - 1
        },
        frameStyle() {
            return {
                top: this.frame.top + 'px',
                left: this.frame.left + 'px',
                width: this.frame.width + 'px',
                height: this.frame.height + 'px'
            }
        },
        imageBarStyle() {
            return {
                top: Math.max(this.frame.top + 8, 0) + 'px',
                left: Math.max(this.frame.left + 8, 0) + 'px'
            }
        }
    },
    watch: {
        value(html) {
            if (html !== this.lastEmitted) {
                this.load(html)
            }
        }
    },
    created() {
        // Fuera de data: no tienen que ser reactivos.
        this.history = []
        this.lastEmitted = null
        this.savedRange = null
        this.pendingImageMode = null
        this.lastRecord = { kind: '', time: 0 }
    },
    mounted() {
        this.load(this.value)
        this.useParagraphs()

        // La barra se queda fija justo debajo de la cabecera del panel al bajar
        // por páginas largas.
        const appBar = document.querySelector('.v-app-bar')
        if (appBar) {
            this.stickyTop = appBar.offsetHeight
        }

        document.addEventListener('selectionchange', this.onSelectionChange)
        window.addEventListener('resize', this.updateFrame)

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updateFrame())
            this.resizeObserver.observe(this.$refs.content)
        }
    },
    beforeDestroy() {
        document.removeEventListener('selectionchange', this.onSelectionChange)
        window.removeEventListener('resize', this.updateFrame)

        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    },
    methods: {
        // ---- Contenido e historial -------------------------------------------

        load(html) {
            // Una página nueva arranca con un párrafo vacío, como en cualquier
            // procesador de texto: así Intro crea párrafos y no saltos sueltos.
            this.render((html ?? '').trim() === '' ? '<p><br></p>' : html)
            this.lastEmitted = html
            // La primera entrada guarda el valor ORIGINAL: deshacer hasta el
            // principio devuelve el cuerpo exacto, no su versión re-serializada.
            this.history = [{ html: this.$refs.content.innerHTML, value: html, selection: null }]
            this.historyIndex = 0
            this.historyLength = 1
            this.deselectImage()
            this.activeBlock = null
        },
        render(html) {
            const content = this.$refs.content
            content.innerHTML = html

            // Los <style> que traen algunas páginas antiguas se aplicarían a todo
            // el panel mientras se edita. Se desactiva la hoja, no la etiqueta:
            // el HTML que se guarda no cambia.
            content.querySelectorAll('style').forEach(style => {
                style.disabled = true
            })
        },
        record(kind) {
            const html = this.$refs.content.innerHTML
            const current = this.history[this.historyIndex]

            if (current && current.html === html) {
                return
            }

            const now = Date.now()
            const entry = { html, value: html, selection: this.saveSelection() }
            const merge = kind === this.lastRecord.kind
                && ['typing', 'resize'].includes(kind)
                && now - this.lastRecord.time < MERGE_MS
                && this.historyIndex > 0
                && this.historyIndex === this.history.length - 1

            if (merge) {
                this.history[this.historyIndex] = entry
            } else {
                this.history.splice(this.historyIndex + 1)
                this.history.push(entry)
                if (this.history.length > HISTORY_LIMIT) {
                    this.history.shift()
                }
                this.historyIndex = this.history.length - 1
            }

            this.historyLength = this.history.length
            this.lastRecord = { kind, time: now }
            this.emit(html)
        },
        emit(html) {
            this.lastEmitted = html
            this.$emit('input', html)
        },
        undo() {
            if (this.canUndo) {
                this.restore(this.historyIndex - 1)
            }
        },
        redo() {
            if (this.canRedo) {
                this.restore(this.historyIndex + 1)
            }
        },
        restore(index) {
            const entry = this.history[index]

            this.historyIndex = index
            this.lastRecord = { kind: '', time: 0 }
            this.deselectImage()
            this.render(entry.html)
            this.restoreSelection(entry.selection)
            this.emit(entry.value)
        },

        // ---- Selección -------------------------------------------------------

        currentRange() {
            const selection = window.getSelection()

            if (!selection || !selection.rangeCount) {
                return null
            }

            const range = selection.getRangeAt(0)

            return this.$refs.content.contains(range.commonAncestorContainer) ? range : null
        },
        setRange(range) {
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
            this.savedRange = range.cloneRange()
        },
        // Rango guardado, o el final del contenido si nunca se ha puesto el
        // cursor dentro.
        insertionRange() {
            const content = this.$refs.content

            if (this.savedRange && content.contains(this.savedRange.startContainer)) {
                return this.savedRange.cloneRange()
            }

            const range = document.createRange()
            range.selectNodeContents(content)
            range.collapse(false)

            return range
        },
        onSelectionChange() {
            const range = this.currentRange()

            if (!range) {
                return
            }

            this.savedRange = range.cloneRange()
            this.activeBlock = this.gridItemOf(range.startContainer)

            // Si el usuario mueve el cursor fuera de la imagen, deja de estar
            // seleccionada.
            if (this.selectedImage && !this.rangeIsNode(range, this.selectedImage)) {
                this.deselectImage()
            }
        },
        rangeIsNode(range, node) {
            const parent = node.parentNode
            const index = Array.prototype.indexOf.call(parent.childNodes, node)

            return range.startContainer === parent && range.endContainer === parent
                && range.startOffset === index && range.endOffset === index + 1
        },
        saveSelection() {
            const range = this.currentRange()

            if (!range) {
                return null
            }

            return {
                start: this.pathTo(range.startContainer),
                startOffset: range.startOffset,
                end: this.pathTo(range.endContainer),
                endOffset: range.endOffset
            }
        },
        restoreSelection(saved) {
            if (!saved) {
                return
            }

            const start = this.nodeAt(saved.start)
            const end = this.nodeAt(saved.end)

            if (!start || !end) {
                return
            }

            try {
                const range = document.createRange()
                range.setStart(start, Math.min(saved.startOffset, this.nodeLength(start)))
                range.setEnd(end, Math.min(saved.endOffset, this.nodeLength(end)))
                this.setRange(range)
            } catch (e) {
                // La posición ya no existe: el cursor se queda donde esté.
            }
        },
        pathTo(node) {
            const path = []
            const content = this.$refs.content

            while (node && node !== content) {
                path.unshift(Array.prototype.indexOf.call(node.parentNode.childNodes, node))
                node = node.parentNode
            }

            return path
        },
        nodeAt(path) {
            let node = this.$refs.content

            for (const index of path) {
                node = node && node.childNodes[index]
            }

            return node || null
        },
        nodeLength(node) {
            return node.nodeType === Node.TEXT_NODE ? node.data.length : node.childNodes.length
        },

        // ---- Estructura del diseño -------------------------------------------

        elementOf(node) {
            return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
        },
        // Bloque de diseño que contiene la posición: la tarjeta, la rejilla o,
        // fuera de todo bloque, el propio editor.
        layoutBlockOf(node) {
            const content = this.$refs.content
            let element = this.elementOf(node)

            while (element && element !== content) {
                if (LAYOUT_BLOCKS.includes(element.tagName)) {
                    return element
                }
                element = element.parentElement
            }

            return content
        },
        // Elemento hijo directo de una rejilla o fila flexible: un premio de la
        // página de premios. Es lo que se duplica o se quita entero.
        gridItemOf(node) {
            const content = this.$refs.content
            let element = this.elementOf(node)

            while (element && element !== content) {
                const parent = element.parentElement

                if (parent && parent !== content.parentElement && content.contains(parent)) {
                    const display = window.getComputedStyle(parent).display

                    if (['grid', 'inline-grid', 'flex', 'inline-flex'].includes(display)) {
                        return element
                    }
                }
                element = parent
            }

            return null
        },
        crossesLayout(range) {
            return this.layoutBlockOf(range.startContainer) !== this.layoutBlockOf(range.endContainer)
        },
        inTextBlock(node) {
            const content = this.$refs.content
            let element = this.elementOf(node)

            while (element && element !== content) {
                if (TEXT_BLOCKS.includes(element.tagName)) {
                    return true
                }
                if (LAYOUT_BLOCKS.includes(element.tagName)) {
                    return false
                }
                element = element.parentElement
            }

            return false
        },
        preservesNewlines(node) {
            const element = this.elementOf(node)

            return /^(pre|pre-wrap|pre-line|break-spaces)$/.test(window.getComputedStyle(element).whiteSpace)
        },
        warnLayout() {
            this.$store.dispatch('snackbar/showSnackbar', {
                color: 'warning',
                message: this.$t('layoutEditor.layoutProtected')
            })
        },

        // ---- Eventos del editor ----------------------------------------------

        onBeforeInput(event) {
            const type = event.inputType

            if (type === 'historyUndo' || type === 'historyRedo') {
                event.preventDefault()
                type === 'historyUndo' ? this.undo() : this.redo()

                return
            }

            const ranges = event.getTargetRanges ? event.getTargetRanges() : []
            const range = ranges[0] || this.currentRange()

            if (!range) {
                return
            }

            if (type === 'insertParagraph') {
                // Partir un párrafo está bien; partir una tarjeta crearía una
                // tarjeta nueva a medias dentro de la rejilla.
                if (this.inTextBlock(range.startContainer)) {
                    this.useParagraphs()
                } else {
                    event.preventDefault()
                    document.execCommand('insertLineBreak')
                }

                return
            }

            if (this.crossesLayout(range)) {
                event.preventDefault()
                this.warnLayout()
            }
        },
        onInput(event) {
            const type = event.inputType || ''
            const typing = type.startsWith('insertText') || type.startsWith('delete')

            if (this.selectedImage && !this.selectedImage.isConnected) {
                this.deselectImage()
            }

            this.liftBlocksOutOfParagraphs()
            this.record(typing ? 'typing' : 'edit')
            this.updateFrame()
        },
        // Chrome crea las listas DENTRO del párrafo en el que está el cursor
        // (<p><ul>…</ul></p>), y al salir de la lista mete el párrafo nuevo
        // también dentro. Ese HTML no es válido: al publicarlo, el navegador lo
        // recoloca y la página no se ve como en el editor. Se deja ya como
        // quedará: el párrafo se parte y los bloques quedan a su altura. El
        // HTML cargado de la base de datos nunca trae este anidamiento, así que
        // esto solo toca lo que acaba de crear el propio navegador.
        liftBlocksOutOfParagraphs() {
            const nested = this.$refs.content.querySelectorAll(NESTED_BLOCKS_SELECTOR)

            if (!nested.length) {
                return
            }

            const range = this.currentRange()
            const caret = range && [range.startContainer, range.startOffset, range.endContainer, range.endOffset]
            const paragraphs = new Set(Array.prototype.map.call(nested, block => block.parentElement))

            // De dentro afuera, por si hay párrafos anidados en varios niveles.
            Array.from(paragraphs).reverse().forEach(paragraph => {
                const parent = paragraph.parentNode
                let inline = null

                // Lo que no es bloque se queda en un párrafo igual que el original
                // (mismos estilos); los bloques salen a su altura.
                Array.from(paragraph.childNodes).forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE && PARAGRAPH_BREAKERS.includes(node.tagName)) {
                        inline = null
                        parent.insertBefore(node, paragraph)
                    } else {
                        inline = inline || parent.insertBefore(paragraph.cloneNode(false), paragraph)
                        inline.appendChild(node)
                    }
                })

                paragraph.remove()
            })

            if (caret) {
                try {
                    const restored = document.createRange()
                    restored.setStart(caret[0], caret[1])
                    restored.setEnd(caret[2], caret[3])
                    this.setRange(restored)
                } catch (e) {
                    // El cursor estaba en algo que ya no existe: se queda donde esté.
                }
            }
        },
        onKeydown(event) {
            const mod = event.ctrlKey || event.metaKey
            const key = event.key.toLowerCase()

            if (mod && key === 'z') {
                event.preventDefault()
                event.shiftKey ? this.redo() : this.undo()
            } else if (mod && key === 'y') {
                event.preventDefault()
                this.redo()
            } else if (event.key === 'Escape') {
                this.deselectImage()
            }
        },
        onContentClick(event) {
            const target = event.target

            // Algunas páginas antiguas llevan onclick (los botones de vídeo de la
            // portada): mientras se edita no deben ejecutarse, ni los enlaces
            // navegar.
            if (target.closest('[onclick]')) {
                event.stopPropagation()
            }
            if (target.closest('a, [onclick]')) {
                event.preventDefault()
            }

            if (target.tagName === 'IMG') {
                this.selectImage(target)
            }
        },
        onPaste(event) {
            event.preventDefault()

            const files = this.imageFiles(event.clipboardData && event.clipboardData.files)

            if (files.length) {
                this.insertUploadedImages(files, this.insertionRange())

                return
            }

            const text = event.clipboardData ? event.clipboardData.getData('text/plain') : ''

            if (text) {
                this.insertText(text)
            }
        },
        onDragOver(event) {
            if (event.dataTransfer && Array.prototype.includes.call(event.dataTransfer.types, 'Files')) {
                event.preventDefault()
            }
        },
        onDrop(event) {
            const files = this.imageFiles(event.dataTransfer && event.dataTransfer.files)

            // Sin ficheros es un arrastre interno (mover una imagen o un texto):
            // lo resuelve el navegador y lo recoge onInput.
            if (!files.length) {
                return
            }

            event.preventDefault()
            this.insertUploadedImages(files, this.rangeFromPoint(event.clientX, event.clientY))
        },
        rangeFromPoint(x, y) {
            let range = null

            if (document.caretRangeFromPoint) {
                range = document.caretRangeFromPoint(x, y)
            } else if (document.caretPositionFromPoint) {
                const position = document.caretPositionFromPoint(x, y)
                if (position) {
                    range = document.createRange()
                    range.setStart(position.offsetNode, position.offset)
                    range.collapse(true)
                }
            }

            return range && this.$refs.content.contains(range.startContainer) ? range : this.insertionRange()
        },
        imageFiles(list) {
            return Array.prototype.filter.call(list || [], file => IMAGE_TYPES.split(',').includes(file.type))
        },

        // ---- Texto -----------------------------------------------------------

        // Los botones de la barra no se llevan el foco (mousedown.prevent), así
        // que normalmente la selección sigue en el editor. Si se ha perdido (un
        // diálogo, un menú), se recupera la última que hubo dentro.
        focusEditor() {
            const content = this.$refs.content
            const current = this.currentRange()

            content.focus()
            if (current) {
                this.setRange(current)
            } else if (this.savedRange && content.contains(this.savedRange.startContainer)) {
                this.setRange(this.savedRange)
            }

            return this.currentRange()
        },
        // <p> y no <div> cuando el navegador tiene que crear un párrafo (Intro
        // detrás de un título, centrar una línea suelta...): un <div> nuevo
        // contaría como bloque de diseño. Es un ajuste del documento entero.
        useParagraphs() {
            document.execCommand('defaultParagraphSeparator', false, 'p')
        },
        exec(command, value = null) {
            this.useParagraphs()
            document.execCommand('styleWithCSS', false, false)
            document.execCommand(command, false, value)
        },
        format(command) {
            if (this.focusEditor()) {
                this.exec(command)
            }
        },
        setColor(color) {
            if (!this.focusEditor()) {
                return
            }

            // Con styleWithCSS el color sale como <span style="color">; sin él,
            // como <font color>, que el saneador del servidor no admite.
            document.execCommand('styleWithCSS', false, true)
            document.execCommand('foreColor', false, color)
            document.execCommand('styleWithCSS', false, false)
        },
        // Alineación y listas.
        formatBlock(command) {
            const range = this.focusEditor()

            if (!range) {
                return
            }
            if (this.crossesLayout(range)) {
                this.warnLayout()

                return
            }

            this.exec(command)
        },
        // Normal / Título 1-3. Si el cursor está en un párrafo o título, se le
        // cambia solo la etiqueta (conserva sus estilos y su contenido); si está
        // en texto suelto, lo envuelve el navegador.
        setTextStyle(tag) {
            const range = this.focusEditor()

            if (!range) {
                return
            }
            if (this.crossesLayout(range)) {
                this.warnLayout()

                return
            }

            const blocks = this.textBlocksIn(range)

            if (!blocks.length) {
                if (this.layoutBlockOf(range.startContainer) !== this.$refs.content) {
                    // Dentro de una tarjeta el navegador convertiría la tarjeta
                    // entera en título y la sacaría de la rejilla.
                    this.warnLayout()

                    return
                }

                this.exec('formatBlock', '<' + tag.toLowerCase() + '>')

                return
            }

            const selection = this.saveSelection()

            blocks.forEach(block => {
                if (block.tagName === tag) {
                    return
                }

                const replacement = document.createElement(tag)
                Array.prototype.forEach.call(block.attributes, attribute => {
                    replacement.setAttribute(attribute.name, attribute.value)
                })
                while (block.firstChild) {
                    replacement.appendChild(block.firstChild)
                }
                block.parentNode.replaceChild(replacement, block)
            })

            this.restoreSelection(selection)
            this.record('edit')
        },
        textBlockOf(node) {
            const content = this.$refs.content
            let element = this.elementOf(node)

            while (element && element !== content && !LAYOUT_BLOCKS.includes(element.tagName)) {
                if (/^(P|H[1-6])$/.test(element.tagName)) {
                    return element
                }
                element = element.parentElement
            }

            return null
        },
        textBlocksIn(range) {
            const first = this.textBlockOf(range.startContainer)

            if (range.collapsed) {
                return first ? [first] : []
            }

            return Array.prototype.filter.call(
                this.$refs.content.querySelectorAll('p, h1, h2, h3, h4, h5, h6'),
                block => range.intersectsNode(block)
            )
        },
        // Texto pegado: se inserta a mano para que los saltos de línea no se
        // conviertan en bloques nuevos. Donde el diseño respeta los saltos
        // (white-space: pre-wrap) entran tal cual; dentro de un bloque, como <br>.
        insertText(text) {
            const range = this.currentRange() || this.insertionRange()

            if (this.crossesLayout(range)) {
                this.warnLayout()

                return
            }

            const keepNewlines = this.preservesNewlines(range.startContainer)
            const fragment = document.createDocumentFragment()
            let last = null

            text.replace(/\r\n?/g, '\n').split('\n').forEach((line, index) => {
                if (index > 0) {
                    last = fragment.appendChild(keepNewlines ? document.createTextNode('\n') : document.createElement('br'))
                }
                if (line) {
                    last = fragment.appendChild(document.createTextNode(line))
                }
            })

            if (!last) {
                return
            }

            range.deleteContents()
            range.insertNode(fragment)

            const after = document.createRange()
            after.setStartAfter(last)
            after.collapse(true)
            this.setRange(after)

            this.record('edit')
        },

        // ---- Imágenes --------------------------------------------------------

        selectImage(image) {
            this.selectedImage = image
            this.selectedWidth = Math.round(image.getBoundingClientRect().width)

            const range = document.createRange()
            range.selectNode(image)
            this.setRange(range)

            this.activeBlock = this.gridItemOf(image)
            this.updateFrame()
        },
        deselectImage() {
            this.selectedImage = null
        },
        updateFrame() {
            const image = this.selectedImage

            if (!image) {
                return
            }
            if (!image.isConnected) {
                this.deselectImage()

                return
            }

            const stage = this.$refs.stage.getBoundingClientRect()
            const rect = image.getBoundingClientRect()

            this.frame = {
                top: rect.top - stage.top,
                left: rect.left - stage.left,
                width: rect.width,
                height: rect.height
            }
            this.selectedWidth = Math.round(rect.width)
        },
        pickImage(mode) {
            this.pendingImageMode = mode
            this.$refs.imageInput.value = ''
            this.$refs.imageInput.click()
        },
        async onImagePicked(event) {
            const file = event.target.files && event.target.files[0]
            const mode = this.pendingImageMode

            if (!file) {
                return
            }

            if (mode === 'replace' && this.selectedImage) {
                await this.replaceImage(this.selectedImage, file)
            } else {
                await this.insertUploadedImages([file], this.insertionRange())
            }
        },
        async uploadImage(file) {
            const url = await this.upload(file)
            const image = await this.loadImage(url)

            return { url, naturalWidth: image.naturalWidth }
        },
        loadImage(url) {
            return new Promise(resolve => {
                const image = new Image()
                image.onload = () => resolve(image)
                image.onerror = () => resolve(image)
                image.src = url
            })
        },
        async replaceImage(image, file) {
            this.busy = true
            try {
                const { url } = await this.uploadImage(file)

                // Mismo sitio y mismo ancho: solo cambia la foto. Sin el alto
                // fijo, para que la nueva no se deforme si su proporción es otra.
                image.setAttribute('src', url)
                image.removeAttribute('height')
                this.record('edit')
                this.$nextTick(() => this.selectImage(image))
            } catch (e) {
                this.uploadFailed()
            } finally {
                this.busy = false
            }
        },
        async insertUploadedImages(files, range) {
            if (this.crossesLayout(range)) {
                range.collapse(true)
            }

            this.busy = true
            try {
                let image = null

                for (const file of files) {
                    const { url, naturalWidth } = await this.uploadImage(file)

                    image = document.createElement('img')
                    image.setAttribute('src', url)
                    image.setAttribute('width', String(this.defaultWidth(range, naturalWidth)))

                    if (!range.collapsed) {
                        range.deleteContents()
                    }
                    range.insertNode(image)
                    range.setStartAfter(image)
                    range.collapse(true)
                }

                this.setRange(range)
                this.record('edit')
                this.$nextTick(() => this.selectImage(image))
            } catch (e) {
                this.uploadFailed()
            } finally {
                this.busy = false
            }
        },
        // Una imagen nueva dentro de una rejilla toma el ancho de sus vecinas,
        // para que el premio nuevo quede igual que los demás. Fuera, su tamaño
        // natural sin pasarse del ancho del bloque.
        defaultWidth(range, naturalWidth) {
            const item = this.gridItemOf(range.startContainer)
            const sibling = item && item.parentElement.querySelector('img[width]')
            const siblingWidth = sibling && parseInt(sibling.getAttribute('width'), 10)

            if (siblingWidth) {
                return siblingWidth
            }

            const available = Math.max(this.layoutBlockOf(range.startContainer).clientWidth - 30, MIN_IMAGE_WIDTH)

            return Math.min(naturalWidth || DEFAULT_MAX_IMAGE_WIDTH, available, DEFAULT_MAX_IMAGE_WIDTH)
        },
        resizeImage(direction) {
            const image = this.selectedImage

            if (!image) {
                return
            }

            const current = image.getBoundingClientRect().width
            const max = Math.max(this.layoutBlockOf(image).clientWidth, MIN_IMAGE_WIDTH)
            const next = Math.round(Math.min(Math.max(current * (direction > 0 ? 1.1 : 0.9), MIN_IMAGE_WIDTH), max))

            image.setAttribute('width', String(next))
            image.removeAttribute('height')
            this.record('resize')
            this.updateFrame()
        },
        removeImage() {
            const image = this.selectedImage

            if (!image) {
                return
            }

            const range = document.createRange()
            range.setStartBefore(image)
            range.collapse(true)

            image.remove()
            this.deselectImage()
            this.setRange(range)
            this.record('edit')
        },
        uploadFailed() {
            this.$store.dispatch('snackbar/error', this.$t('layoutEditor.uploadError'))
        },

        // ---- Bloques de la rejilla -------------------------------------------

        duplicateBlock() {
            const block = this.activeBlock

            if (!block || !block.isConnected) {
                return
            }

            const copy = block.cloneNode(true)
            const gap = block.previousSibling

            block.parentNode.insertBefore(copy, block.nextSibling)

            // Conserva la sangría del HTML entre bloques: el código sigue
            // leyéndose igual que el que había.
            if (gap && gap.nodeType === Node.TEXT_NODE && !gap.data.trim()) {
                block.parentNode.insertBefore(gap.cloneNode(), copy)
            }

            this.record('edit')

            const image = copy.querySelector('img')
            this.$nextTick(() => {
                copy.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
                if (image) {
                    this.selectImage(image)
                }
            })
        },
        removeBlock() {
            const block = this.activeBlock

            if (!block || !block.isConnected) {
                return
            }

            const gap = block.previousSibling
            const range = document.createRange()
            range.setStartBefore(block)
            range.collapse(true)

            if (gap && gap.nodeType === Node.TEXT_NODE && !gap.data.trim()) {
                gap.remove()
            }
            block.remove()

            this.deselectImage()
            this.activeBlock = null
            this.setRange(range)
            this.record('edit')
        },

        // ---- Enlaces ---------------------------------------------------------

        openLinkDialog() {
            const range = this.savedRange && this.$refs.content.contains(this.savedRange.startContainer)
                ? this.savedRange.cloneRange()
                : null
            const anchor = range && this.elementOf(range.startContainer).closest('a')
            const ownAnchor = anchor && this.$refs.content.contains(anchor) ? anchor : null

            if (!ownAnchor && (!range || range.collapsed)) {
                this.$store.dispatch('snackbar/showSnackbar', {
                    color: 'warning',
                    message: this.$t('layoutEditor.linkSelectText')
                })

                return
            }

            this.link = {
                dialog: true,
                href: ownAnchor ? ownAnchor.getAttribute('href') : '',
                anchor: ownAnchor,
                range,
                uploading: false,
                error: ''
            }
        },
        async onLinkFilePicked(event) {
            const file = event.target.files && event.target.files[0]
            event.target.value = ''

            if (!file) {
                return
            }

            this.link.uploading = true
            try {
                this.link.href = await this.upload(file)
                this.link.error = ''
            } catch (e) {
                this.link.error = this.$t('layoutEditor.uploadError')
            } finally {
                this.link.uploading = false
            }
        },
        normalizeHref(href) {
            const value = (href || '').trim()

            if (/^(https?:|mailto:|tel:)/i.test(value) || value.startsWith('/')) {
                return value
            }
            if (/^www\./i.test(value)) {
                return 'https://' + value
            }

            return null
        },
        applyLink() {
            const href = this.normalizeHref(this.link.href)

            if (!href) {
                this.link.error = this.$t('layoutEditor.linkInvalid')

                return
            }

            if (this.link.anchor) {
                this.link.anchor.setAttribute('href', href)
            } else {
                const range = this.link.range

                if (this.crossesLayout(range)) {
                    this.link.dialog = false
                    this.warnLayout()

                    return
                }

                const anchor = document.createElement('a')
                anchor.setAttribute('href', href)
                anchor.setAttribute('target', '_blank')
                anchor.setAttribute('rel', 'noopener noreferrer')
                anchor.appendChild(range.extractContents())
                range.insertNode(anchor)
            }

            this.link.dialog = false
            this.record('edit')
        },
        removeLink() {
            const anchor = this.link.anchor

            if (anchor && anchor.isConnected) {
                while (anchor.firstChild) {
                    anchor.parentNode.insertBefore(anchor.firstChild, anchor)
                }
                anchor.remove()
                this.record('edit')
            }

            this.link.dialog = false
        }
    }
}
</script>

<style>
/* Las clases que dejó Quill en las páginas ya existentes (ql-align-center,
   ql-indent-1, ql-size-large, listas...) se ven igual que en la página publicada,
   que carga la hoja completa de Quill. */
@import '~quill/dist/quill.core.css';
</style>

<style scoped>
.layout-editor__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 4px;
    border-radius: 4px 4px 0 0;
    position: sticky;
    z-index: 3;
}

.layout-editor__help {
    padding: 4px 8px 0;
    border-left: 1px solid rgba(128, 128, 128, 0.4);
    border-right: 1px solid rgba(128, 128, 128, 0.4);
    margin: 0;
}

.layout-editor__stage {
    position: relative;
    border: 1px solid rgba(128, 128, 128, 0.4);
    border-radius: 0 0 4px 4px;
}

/* Mismo aspecto que en la página publicada (PostView pinta el cuerpo dentro de
   .ql-editor). Se repiten aquí las reglas que importan por si la hoja de Quill
   no está cargada en el panel. */
.layout-editor__content {
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.42;
    tab-size: 4;
    min-height: 240px;
    height: auto;
    overflow: visible;
    outline: none;
}

.layout-editor__content >>> img {
    cursor: pointer;
}

/* Vídeos incrustados: que el clic no se lo quede el reproductor. */
.layout-editor__content >>> iframe {
    pointer-events: none;
}

.layout-editor__swatches {
    display: grid;
    grid-template-columns: repeat(4, 28px);
    gap: 6px;
    padding: 8px;
}

.layout-editor__swatch {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid rgba(128, 128, 128, 0.6);
    cursor: pointer;
}

.layout-editor__frame {
    position: absolute;
    pointer-events: none;
    outline: 3px solid #0646ed;
    outline-offset: 1px;
}

.layout-editor__image-bar {
    position: absolute;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    max-width: calc(100% - 16px);
    padding: 2px 4px;
    border-radius: 4px;
    z-index: 2;
}

.layout-editor__size {
    min-width: 56px;
    text-align: center;
    font-size: 12px;
}
</style>
